"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  CheckIcon,
  XCircle,
  ChevronDown,
  XIcon,
  Search,
  Loader2,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { PopoverPortal } from "@radix-ui/react-popover";

/**
 * Variants for the multi-selector component to handle different styles.
 */
const multiSelectorVariants = cva(
  "m-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300",
  {
    variants: {
      variant: {
        default:
          "border-foreground/10 text-white bg-card hover:bg-card/80",
        secondary:
          "border-foreground/10  text-white hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "border-2 border-primary text-white hover:bg-primary hover:text-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

/**
 * Option interface for the multi-selector
 */
export interface MultiSelectorOption {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
}

/**
 * Search function type for API-based search
 */
export type SearchFunction = (query: string) => Promise<MultiSelectorOption[]>;

/**
 * Props for MultiSelector component
 */
interface MultiSelectorProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectorVariants> {
  /**
   * An array of option objects to be displayed in the multi-selector component.
   * These are always shown and can be selected without searching.
   */
  options?: MultiSelectorOption[];

  /**
   * Search function for API-based search.
   * When provided, search results will be combined with default options.
   */
  onSearch?: SearchFunction;

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values in format [{code: "value1"}, {code: "value2"}].
   */
  onValueChange: (value: {code: string}[]) => void;

  /**
   * Callback function triggered when search query changes.
   * Useful for debouncing or other search optimizations.
   */
  onSearchChange?: (query: string) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Placeholder text for the search input.
   * Optional, defaults to "Search...".
   */
  searchPlaceholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-selector component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Debounce delay for search in milliseconds.
   * Optional, defaults to 300ms.
   */
  debounceDelay?: number;

  /**
   * Minimum characters required to trigger search.
   * Optional, defaults to 1.
   */
  minSearchLength?: number;

  /**
   * Maximum height for the options list.
   * Optional, defaults to 200px.
   */
  maxHeight?: string;

  /**
   * Additional class names to apply custom styles to the multi-selector component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * Loading state for API search.
   * Optional, defaults to false.
   */
  loading?: boolean;

  /**
   * Error state for API search.
   * Optional, defaults to null.
   */
  error?: string | null;

  /**
   * Empty state message when no results are found.
   * Optional, defaults to "No results found."
   */
  emptyMessage?: string;
}

export const MultiSelector = React.forwardRef<
  HTMLButtonElement,
  MultiSelectorProps
>(
  (
    {
      options = [],
      onSearch,
      onValueChange,
      onSearchChange,
      variant,
      defaultValue = [],
      placeholder = "Select options",
      searchPlaceholder = "Search...",
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      asChild = false,
      debounceDelay = 300,
      minSearchLength = 1,
      maxHeight = "200px",
      className,
      loading = false,
      error = null,
      emptyMessage = "No results found.",
      ...props
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] = React.useState<string[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");
    const [searchResults, setSearchResults] = React.useState<MultiSelectorOption[]>([]);
    const [allAvailableOptions, setAllAvailableOptions] = React.useState<MultiSelectorOption[]>([]);
    const [isSearching, setIsSearching] = React.useState(false);
    const [searchError, setSearchError] = React.useState<string | null>(null);

    const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Debounced search function
    const debouncedSearch = React.useCallback(
      (query: string) => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(async () => {
          if (query.length < minSearchLength) {
            setSearchResults([]);
            return;
          }

          if (onSearch) {
            // API-based search
            setIsSearching(true);
            setSearchError(null);
            try {
              const results = await onSearch(query);
              console.log("API Search Results:", results);
              setSearchResults(results);
              
              // Add new API results to allAvailableOptions if they don't exist
              setAllAvailableOptions(prev => {
                const newOptions = [...prev];
                results.forEach(result => {
                  if (!newOptions.some(option => option.value === result.value)) {
                    newOptions.push(result);
                  }
                });
                return newOptions;
              });
            } catch (err) {
              setSearchError(err instanceof Error ? err.message : "Search failed");
              setSearchResults([]);
            } finally {
              setIsSearching(false);
            }
          } else {
            // Default data-based search
            const filtered = options.filter((option) =>
              option.label.toLowerCase().includes(query.toLowerCase())
            );
            setSearchResults(filtered);
          }
        }, debounceDelay);
      },
      [onSearch, options, debounceDelay, minSearchLength]
    );

    // Handle search input change
    const handleSearchChange = (value: string) => {
      setSearchQuery(value);
      onSearchChange?.(value);
      debouncedSearch(value);
    };

    // Get current options to display - combine default options with search results
    const currentOptions = React.useMemo(() => {
      if (searchQuery.length >= minSearchLength && onSearch) {
        // When searching with API, show search results first, then add default options that match
        const combinedOptions = [...searchResults];
        
        // Add default options that match the search query and aren't already in search results
        options.forEach(defaultOption => {
          if (defaultOption.label.toLowerCase().includes(searchQuery.toLowerCase()) &&
              !searchResults.some(searchResult => searchResult.value === defaultOption.value)) {
            combinedOptions.push(defaultOption);
          }
        });
        
        console.log("Current Options (with search):", combinedOptions);
        return combinedOptions;
      } else if (searchQuery.length >= minSearchLength && !onSearch) {
        // When searching without API, filter default options
        const filtered = options.filter((option) =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
        console.log("Current Options (filtered):", filtered);
        return filtered;
      }
      
      // No search query or query too short, show all default options
      console.log("Current Options (default):", options);
      return options;
    }, [searchQuery, minSearchLength, searchResults, options, onSearch]);

    // Initialize allAvailableOptions with default options
    React.useEffect(() => {
      setAllAvailableOptions(options);
    }, [options]);

    // Handle input key down events
    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        const formattedValues = newSelectedValues.map(value => ({ code: value }));
        onValueChange(formattedValues);
      } else if (event.key === "Escape") {
        setIsPopoverOpen(false);
      }
    };

    // Toggle option selection
    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option];
      setSelectedValues(newSelectedValues);
      
      // Convert to the required format [{code: "value1"}, {code: "value2"}]
      const formattedValues = newSelectedValues.map(value => ({ code: value }));
      onValueChange(formattedValues);
    };

    // Clear all selections
    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    // Toggle popover
    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
      if (!isPopoverOpen) {
        // Focus input when opening
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    };

    // Clear extra options beyond maxCount
    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      const formattedValues = newSelectedValues.map(value => ({ code: value }));
      onValueChange(formattedValues);
    };

    // Toggle all options
    const toggleAll = () => {
      if (selectedValues.length === currentOptions.length) {
        handleClear();
      } else {
        const allValues = currentOptions.map((option) => option.value);
        setSelectedValues(allValues);
        const formattedValues = allValues.map(value => ({ code: value }));
        onValueChange(formattedValues);
      }
    };

    // Get selected options for display
    const getSelectedOptions = () => {
      return selectedValues.map((value) => {
        // Check in allAvailableOptions which includes both default and previously searched options
        const option = allAvailableOptions.find((o) => o.value === value);
        return option;
      }).filter(Boolean) as MultiSelectorOption[];
    };

    // Cleanup timeout on unmount
    React.useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    // Reset search when popover closes
    React.useEffect(() => {
      if (!isPopoverOpen) {
        setSearchQuery("");
        setSearchResults([]);
        setSearchError(null);
      }
    }, [isPopoverOpen]);

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              "flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit [&_svg]:pointer-events-auto",
              className
            )}
          >
            {selectedValues.length > 0 ? (
              <div className="flex justify-between items-center w-full">
                <div className="flex flex-wrap items-center">
                  {selectedValues.slice(0, maxCount).map((value) => {
                    const option = getSelectedOptions().find((o) => o.value === value);
                    
                    return (
                      <Badge
                        key={value}
                        className={cn(
                          isAnimating ? "animate-bounce" : "",
                          multiSelectorVariants({ variant })
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {option?.label}
                        <XCircle
                          className="ml-2 h-4 w-4 cursor-pointer"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleOption(value);
                          }}
                        />
                      </Badge>
                    );
                  })}
                  {selectedValues.length > maxCount && (
                    <Badge
                      className={cn(
                        "bg-primary text-white border-foreground/1 hover:bg-transparent",
                        isAnimating ? "animate-bounce" : "",
                        multiSelectorVariants({ variant })
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      {`+ ${selectedValues.length - maxCount} more`}
                      <XCircle
                        className="ml-2 h-4 w-4 cursor-pointer"
                        onClick={(event) => {
                          event.stopPropagation();
                          clearExtraOptions();
                        }}
                      />
                    </Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <XIcon
                    className="h-4 mx-2 cursor-pointer text-muted-foreground"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}
                  />
                  <Separator
                    orientation="vertical"
                    className="flex min-h-6 h-full"
                  />
                  <ChevronDown className="h-4 mx-2 cursor-pointer text-muted-foreground" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full mx-auto">
                <span className="text-sm text-muted-foreground mx-3">
                  {placeholder}
                </span>
                <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverPortal>
        <PopoverContent
          className="w-[200px] p-0 relative"
          side="top"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <div className="p-2">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleInputKeyDown}
                className="pl-8 pr-8"
              />
              {(isSearching || loading) && (
                <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>

            {/* Error State */}
            {(error || searchError) && (
              <div className="p-2 text-sm text-destructive">
                {error || searchError}
              </div>
            )}

            {/* Options List */}
            <ScrollArea className="w-full overflow-y-auto" style={{ maxHeight }}>
              <div className="p-1">
                {/* Select All Option */}
                {currentOptions.length > 0 && (
                  <div
                    className="flex items-center p-2 cursor-pointer hover:bg-accent rounded-sm"
                    onClick={toggleAll}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        selectedValues.length === currentOptions.length
                          ? "bg-primary text-white"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    <span className="text-sm">(Select All)</span>
                  </div>
                )}

                {/* Individual Options */}
                {currentOptions.length > 0 ? (
                  currentOptions.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <div
                        key={option.value}
                        className={cn(
                          "flex items-center p-2 cursor-pointer hover:bg-accent rounded-sm",
                          option.disabled && "opacity-50 cursor-not-allowed"
                        )}
                        onClick={() => !option.disabled && toggleOption(option.value)}
                      >
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            isSelected
                              ? "bg-primary text-white"
                              : "opacity-50 [&_svg]:invisible"
                          )}
                        >
                          <CheckIcon className="h-4 w-4" />
                        </div>
                        {option.icon && (
                          <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="text-sm">{option.label}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    {emptyMessage}
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Action Buttons */}
            <div className="flex items-center justify-between border-t px-2">
              {selectedValues.length > 0 && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClear}
                    className="flex-1"
                  >
                    Clear
                  </Button>
                  <Separator orientation="vertical" className="mx-2 h-6" />
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsPopoverOpen(false)}
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </PopoverContent>
        </PopoverPortal>
      </Popover>
    );
  }
);

MultiSelector.displayName = "MultiSelector";
